import { useEffect, useId, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import {
  SOURCE_CALLS,
  SOURCE_KIND_LABEL,
  SOURCE_KINDS,
  type SourceCall,
  type SourceDropReason,
  type SourceKind,
  type SourceSettings,
  type SourceStatus,
} from "@metrickle/schema/constants";
import { api, useApiMutation } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtAgo, fmtNumber, fmtTime } from "../lib/format";
import { track } from "../lib/track";
import { Button, CardSection, Checkbox, CodeBlock, Disclosure, Field, FieldError, Input, Modal, RadioCards, notifyError, toaster, useConfirm, ErrorState, Skeleton } from "./ui";

/**
 * Integrations page: Segment and RudderStack as event sources. Their webhook destinations post the
 * Segment spec to the app's source endpoint, so an app can start without Metrickle's script.
 */

type Credentials = SourceStatus & { url: string; secret: string };

const DOCS = "https://metrickle.com/developers/event-sources#use-a-source-and-the-sdk-together";

const CALL_LABEL: Record<SourceCall, string> = { page: "Page", screen: "Screen", track: "Track", identify: "Identify", alias: "Alias", group: "Group" };
const CALL_HINT: Record<SourceCall, string> = {
  page: "Page views, with path, referrer and UTM",
  screen: "Screen views from mobile apps",
  track: "Custom events, including Order Completed and Order Refunded as revenue",
  identify: "Links a device to a signed-in user",
  alias: "Links a previous anonymous id to a user",
  group: "Stored as a $group event with the group id",
};

const DROP_LABEL: Record<SourceDropReason, string> = {
  invalid: "not a valid message",
  unsupported: "unsupported call",
  call_off: "call turned off",
  opted_out: "integrations flag off",
  no_consent: "no consent",
  bot: "from a bot",
  origin: "page outside the app's domains",
  too_old: "older than 7 days",
};

const ERROR_LABEL: Record<string, string> = {
  unauthorized: "wrong secret or signature",
  invalid_json: "body wasn't JSON",
  too_many_messages: "more than 500 messages in one request",
};

const dropSummary = (dropped: Partial<Record<SourceDropReason, number>>) =>
  Object.entries(dropped)
    .map(([r, n]) => `${fmtNumber(n)} ${DROP_LABEL[r as SourceDropReason] ?? r}`)
    .join(", ");

/** One line on what a source last did, for the list and the setup dialog. */
function lastSeen(s: SourceStatus): string {
  if (!s.configured) return "Not set up";
  const refused = s.lastErrorAt && (!s.lastRequestAt || s.lastErrorAt > s.lastRequestAt);
  if (refused) return `Last request refused ${fmtAgo(s.lastErrorAt!)}: ${ERROR_LABEL[s.lastError ?? ""] ?? s.lastError}`;
  if (s.lastEventAt) return `Last event ${fmtAgo(s.lastEventAt)}${s.lastEventType ? ` · ${CALL_LABEL[s.lastEventType]}` : ""}`;
  if (s.lastRequestAt && s.lastResult) return `Last request ${fmtAgo(s.lastRequestAt)}: nothing stored (${dropSummary(s.lastResult.dropped) || "empty"})`;
  return "Set up, no events yet";
}

export const useSources = (appId: string, live = false) =>
  useQuery({
    queryKey: ["sources", appId],
    queryFn: () => api<SourceStatus[]>(`/apps/${appId}/sources`),
    // Fast while a setup dialog is open, so the live check shows the first event within seconds.
    refetchInterval: live ? 5_000 : 60_000,
  });

export function EventSourcesSection({ appId }: { appId: string }) {
  const can = useCan(appId);
  const [open, setOpen] = useState<SourceKind | null>(null);
  const status = useSources(appId, open !== null);

  return (
    <CardSection flush>
      <ul className="divide-y divide-line">
        {SOURCE_KINDS.map((kind) => {
          const s = status.data?.find((x) => x.kind === kind);
          return (
            <li key={kind} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{SOURCE_KIND_LABEL[kind]}</p>
                {s ? (
                  <p className="text-xs text-ink-2">{lastSeen(s)}</p>
                ) : (
                  <div aria-busy="true">
                    <Skeleton className="mt-1 h-3 w-40" />
                    <span className="sr-only">Loading…</span>
                  </div>
                )}
              </div>
              {can("apps.keys") && (
                <Button
                  size="sm"
                  onClick={() => {
                    setOpen(kind);
                    // The list may be a minute old; the live check should start from now.
                    void status.refetch();
                  }}
                  aria-label={`${s?.configured ? "Manage" : "Set up"} ${SOURCE_KIND_LABEL[kind]}`}>
                  {s?.configured ? "Manage" : "Set up"}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3">
        Pages, events, revenue and sign-ins only. Friction, accessibility settings, replays, heatmaps and surveys need Metrickle's script or SDK on the device.{" "}
        <a className="text-accent-text underline underline-offset-2" href={DOCS} target="_blank" rel="noreferrer">
          Running both<span className="sr-only"> (opens in a new tab)</span>
        </a>
      </p>
      <Modal
        open={open !== null}
        onOpenChange={(o) => !o && setOpen(null)}
        title={open ? `Send events from ${SOURCE_KIND_LABEL[open]}` : ""}
        description="Anyone with the secret can add events to this app, so keep it in your destination's settings only."
        wide
      >
        {open && <SourceSetup appId={appId} kind={open} status={status.data?.find((x) => x.kind === open)} onRemoved={() => setOpen(null)} />}
      </Modal>
    </CardSection>
  );
}

const STEPS: Record<SourceKind, string[]> = {
  segment: [
    "In Segment, add the Webhooks (Actions) destination to the source you want to send.",
    "Add a mapping with the Send action for every event type (track, page, screen, identify, alias and group): method POST, the endpoint below, and the default payload, which is the whole event.",
    "Under Headers, add x-metrickle-secret with the secret below. With the classic Webhooks destination instead, put the secret in Shared Secret: Metrickle checks the X-Signature it adds.",
    "Turn the destination on and send a test event from its Event Tester. It shows up below within a few seconds.",
  ],
  rudderstack: [
    "In RudderStack, add a Webhook destination and connect it to your sources.",
    "Set the URL to the endpoint below and the method to POST.",
    "Under Headers, add the key x-metrickle-secret with the secret below as its value.",
    "Save, then send an event from your site or app. It shows up below within a few seconds.",
  ],
};

const testCommand = (c: Credentials) => `curl -X POST "${c.url}" \\
  -H "content-type: application/json" \\
  -H "x-metrickle-secret: ${c.secret}" \\
  -d '{"type":"track","event":"Metrickle Test","anonymousId":"test-visitor","messageId":"test-'"$(date +%s)"'"}'`;

function SourceSetup({ appId, kind, status, onRemoved }: { appId: string; kind: SourceKind; status: SourceStatus | undefined; onRemoved: () => void }) {
  const creds = useQuery({ queryKey: ["source", appId, kind], queryFn: () => api<Credentials>(`/apps/${appId}/sources/${kind}`) });
  const rotate = useApiMutation(() => api<{ secret: string }>(`/apps/${appId}/sources/${kind}/rotate`, { method: "POST" }), [["source", appId, kind], ["sources", appId]]);
  const remove = useApiMutation(() => api(`/apps/${appId}/sources/${kind}`, { method: "DELETE" }), [["sources", appId]]);
  const { confirm, dialog } = useConfirm();
  const headingId = useId();
  const label = SOURCE_KIND_LABEL[kind];

  if (creds.isError) return <ErrorState title="Couldn't load the endpoint" error={creds.error} />;
  if (!creds.data)
    return (
      <div aria-busy="true">
        <Skeleton className="h-40" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  // The list's status refreshes every few seconds while this is open; creating the source was the first read.
  const live = status?.configured ? status : creds.data;

  return (
    <div className="space-y-5">
      <section aria-labelledby={`${headingId}-steps`} className="space-y-2">
        <h3 id={`${headingId}-steps`} className="text-sm font-semibold text-ink">
          Connect {label}
        </h3>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-2">
          {STEPS[kind].map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <CodeBlock code={creds.data.url} label="Endpoint" onCopy={() => track("source_copied", { kind, what: "url" })} />
        <CodeBlock code={creds.data.secret} label="Secret (x-metrickle-secret header)" onCopy={() => track("source_copied", { kind, what: "secret" })} />
        <Disclosure summary={`Test without ${label}`}>
          <CodeBlock code={testCommand(creds.data)} label="shell" language="shell" />
        </Disclosure>
      </section>

      <LiveCheck status={live} headingId={`${headingId}-live`} />

      <SettingsForm appId={appId} kind={kind} initial={live.settings} headingId={`${headingId}-settings`} />

      <section aria-labelledby={`${headingId}-secret`} className="space-y-2 border-t border-line pt-4">
        <h3 id={`${headingId}-secret`} className="text-sm font-semibold text-ink">
          Secret and connection
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="ghost"
            pending={rotate.isPending}
            pendingLabel="Replacing…"
            onClick={async () => {
              if (await confirm({ title: "Replace the secret?", description: `The current secret stops working within a few minutes. Update it in ${label} straight away.`, confirmLabel: "Replace secret" }))
                rotate.mutate(undefined, { onSuccess: () => toaster.success({ title: "New secret", description: `Update it in ${label}.` }), onError: notifyError });
            }}
          >
            <RefreshCw size={13} aria-hidden /> Replace secret
          </Button>
          <Button
            size="sm"
            variant="ghost"
            pending={remove.isPending}
            pendingLabel="Disconnecting…"
            onClick={async () => {
              if (await confirm({ title: `Disconnect ${label}?`, description: `Requests from ${label} are refused from now on. Events already stored stay.`, confirmLabel: "Disconnect" }))
                remove.mutate(undefined, { onSuccess: () => (toaster.success({ title: `${label} disconnected` }), onRemoved()), onError: notifyError });
            }}
          >
            Disconnect
          </Button>
        </div>
      </section>
      {dialog}
    </div>
  );
}

/**
 * Whether events are arriving. Only the waiting → connected change is announced (a polite status);
 * the details under it update quietly, so a busy source doesn't talk over everything else.
 */
function LiveCheck({ status, headingId }: { status: SourceStatus; headingId: string }) {
  const refused = !!status.lastErrorAt && (!status.lastRequestAt || status.lastErrorAt > status.lastRequestAt);
  const connected = !!status.lastEventAt && !refused;
  // Re-render each few seconds so "4s ago" stays true between polls.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 5_000);
    return () => clearInterval(t);
  }, []);

  return (
    <section aria-labelledby={headingId} className="space-y-1.5 rounded-lg border border-line p-3">
      <h3 id={headingId} className="text-sm font-semibold text-ink">
        Live check
      </h3>
      <p role="status" className="flex items-center gap-2 text-sm text-ink">
        {connected ? <CheckCircle2 size={16} className="shrink-0 text-good" aria-hidden /> : refused ? <AlertTriangle size={16} className="shrink-0 text-bad" aria-hidden /> : null}
        {connected ? "Connected: events are arriving." : refused ? "Requests are being refused." : "Waiting for the first event…"}
      </p>
      <p className="text-xs text-ink-2">
        {lastSeen(status)}
        {status.lastEventAt && <span className="sr-only"> ({fmtTime(status.lastEventAt)})</span>}
      </p>
      {status.lastResult && Object.keys(status.lastResult.dropped).length > 0 && (
        <p className="text-xs text-ink-2">
          Last request: {fmtNumber(status.lastResult.accepted)} stored, {dropSummary(status.lastResult.dropped)}.
        </p>
      )}
    </section>
  );
}

function SettingsForm({ appId, kind, initial, headingId }: { appId: string; kind: SourceKind; initial: SourceSettings; headingId: string }) {
  const [calls, setCalls] = useState<SourceCall[]>(initial.calls);
  const [mode, setMode] = useState(initial.consent.mode);
  const [categories, setCategories] = useState(initial.consent.categories.join(", "));
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();
  const callsHintId = useId();
  const save = useApiMutation(
    (s: SourceSettings) => api<SourceStatus>(`/apps/${appId}/sources/${kind}`, { method: "PUT", body: JSON.stringify(s) }),
    [["sources", appId], ["source", appId, kind]],
  );
  const list = categories.split(",").map((c) => c.trim()).filter(Boolean);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!calls.length) return setError("Choose at least one call to accept.");
    if (mode === "required" && !list.length) return setError("List at least one consent category, or store every event.");
    setError(null);
    save.mutate(
      { calls, consent: { mode, categories: mode === "required" ? list : [] } },
      { onSuccess: () => (track("source_saved", { kind, consent: mode }), toaster.success({ title: "Settings saved" })), onError: notifyError },
    );
  };

  return (
    <form onSubmit={submit} aria-labelledby={headingId} className="space-y-4" noValidate>
      <h3 id={headingId} className="text-sm font-semibold text-ink">
        What to store
      </h3>
      <fieldset className="space-y-2" aria-describedby={callsHintId}>
        <legend className="mb-1 text-[13px] font-medium text-ink">Calls to accept</legend>
        <p id={callsHintId} className="text-xs text-ink-3">
          Running Metrickle's script or SDK too? Turn off Page and Screen: the SDK records them with friction and accessibility context, and keeping both would count each view twice.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SOURCE_CALLS.map((c) => (
            <Checkbox key={c} checked={calls.includes(c)} onCheckedChange={(on) => setCalls((cur) => (on ? SOURCE_CALLS.filter((x) => x === c || cur.includes(x)) : cur.filter((x) => x !== c)))} hint={CALL_HINT[c]}>
              {CALL_LABEL[c]}
            </Checkbox>
          ))}
        </div>
      </fieldset>
      <RadioCards
        label="Consent"
        value={mode}
        onValueChange={(v) => setMode(v as SourceSettings["consent"]["mode"])}
        items={[
          { value: "off", label: "Store every event", hint: `For when ${SOURCE_KIND_LABEL[kind]}'s own consent management already filters what it sends, or no consent is needed.` },
          { value: "required", label: "Only events with consent", hint: "Drops events whose consent fields don't grant every category below, and events that have none." },
        ]}
      />
      {mode === "required" && (
        <Field
          label="Consent categories"
          hint={
            kind === "segment"
              ? "Comma-separated category names from context.consent.categoryPreferences, for example Analytics."
              : "Comma-separated consent ids from context.consentManagement, for example C0002 for OneTrust performance cookies."
          }
        >
          <Input
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            placeholder={kind === "segment" ? "Analytics" : "C0002"}
            aria-invalid={!!error && !list.length}
            aria-describedby={error ? errorId : undefined}
          />
        </Field>
      )}
      {error && (
        <FieldError id={errorId} role="alert">
          {error}
        </FieldError>
      )}
      <p className="text-xs text-ink-3">Whatever you choose, an event whose integrations flags turn Metrickle off isn't stored, and bot filtering and the app's domain allowlist apply to pages.</p>
      <Button type="submit" variant="primary" size="sm" pending={save.isPending} pendingLabel="Saving…">
        Save settings
      </Button>
    </form>
  );
}
